import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import { ExperimentationState } from '@interfaces';
import { InvestmentObjectives, IRAOptions, IRATrustTypes } from '@interfaces/models';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { AdobeLaunchState } from '@interfaces/state';
import { EntityState } from '@ngrx/entity';
import * as fromRouter from '@ngrx/router-store';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromOneTimePasscodeState from '@store/reducers/otp/otp.reducer';

import * as fromTestMonkey from '../../../test-monkey/store/reducers/test-monkey.reducer';
import * as fromMultiIT from '../internal-transfers/reducers/multi-internal-transfer.reducer';
import * as fromAccountRegistrations from './account-registrations/account-registrations.reducer';
import * as fromAccountTriage from './account-triage/account-triage.reducer';
import * as fromAdobeLaunch from './adobe-launch/adobe-launch.reducer';
import * as fromAuth from './auth/auth.reducer';
import * as fromBeneficiaries from './beneficiaries/beneficiaries.reducer';
import { Person } from './beneficiaries/beneficiaries.reducer';
import * as fromClientAccounts from './client-accounts/client-accounts.reducer';
import * as fromContent from './content/content.reducer';
import * as fromCostBasis from './cost-basis/cost-basis.reducer';
import * as fromDecedentInfo from './decedent-info/decedent-info.reducer';
import * as fromDocumentUpload from './document-upload/document-upload.reducer';
import * as fromDraft from './draft/draft.reducer';
import * as fromEstate from './estate/estate.reducer';
import * as fromExperimentation from './experimentation/experimentation.reducer';
import * as fromFeature from './feature/feature.reducer';
import { FeatureToggleState } from './feature/feature.reducer';
import * as fromFraudDetectorSimulator from './fraud-detector-simulator/fraud-detector-simulator.reducer';
import * as fromFMF from './fraud-micro-frontend/fmf.reducer';
import * as fromGifting from './gifting/gifting.reducer';
import * as fromIncomeNetWorth from './income-net-worth/income-net-worth.reducer';
import * as fromInheritedIra from './inherited-ira/inherited-ira.reducer';
import * as fromInternalTransfer from './internal-transfer/internal-transfer.reducer';
import * as fromInvestmentObjectives from './investment-objectives/investment-objectives.reducer';
import { INVESTMENT_OBJECTIVES_FEATURE_KEY } from './investment-objectives/investment-objectives.reducer';
import * as fromLetterUpload from './letter-upload/letter-upload.reducer';
import * as fromMfePersonalInfoClient from './mfe-personal-info-client/mfe-personal-info-client.reducer';
import * as fromMinorTransfer from './minor-transfer/minor-transfer.reducer';
import * as fromMultiAccount from './multi-account/multi-account.reducer';
import * as fromMultiTermsAccordion from './multi-terms-accordion/multi-terms-accordion.reducer';
import * as fromMXM from './mxm/mxm.reducer';
import * as fromOpenAccount from './open-account/open-account.reducer';
import * as fromOpenChoice from './open-choice/open-choice.reducer';
import * as fromOrg from './org/org.reducer';
import * as fromPersonalData from './personal-data/personal-data.reducer';
import * as fromMfePersonalAddress from './personal-info-address/personal-info-address.reducers';
import * as fromMfePhone from './personal-phone/personal-phone.reducer';
import * as fromRecipientValidation from './recipient-validation/recipient-validation.reducer';
import * as fromSelectedClientAccounts from './selected-client-accounts/selected-client-accounts.reducer';
import * as fromSpinner from './spinner/spinner.reducer';
import * as fromSubmit from './submit/submit.reducer';
import * as fromTaxDocumentUpload from './tax-document-upload/tax-document-upload.reducer';
import * as fromTCFTrust from './tcf-trust/tcf-trust.reducer';
import * as fromTermsAccordion from './terms-accordion/terms-accordion.reducer';
import {
  adaptedStateReducer,
  transactionIdReducer,
  transactionInvestmentObjectivesEditModeReducer,
  transactionsOAReviewEditModeReducer,
  transactionsReviewEditModeReducer,
} from './transaction-state/transaction-state.reducer';
import * as fromTriage from './triage/triage.reducer';
import * as fromTrust from './trust/trust.reducer';
import * as fromTrustTaxInfo from './trust-tax-info/trust-tax-info.reducer';

export interface RouterStateUrl {
  url: string;
  queryParams: Params;
  params: Params;
}

export interface TransactionState {
  beneficiaries: Person[];
  accountTriage: fromAccountTriage.AccountTriageState & { typeOfTrust?: IRATrustTypes };
  selectedClientAccounts: fromSelectedClientAccounts.ISelectedClientAccountsState;
  currentTransactionId: string;
  currentTransactionEditMode: boolean;
  iraOption?: IRAOptions;
  investmentObjectives?: InvestmentObjectives;
  firstNewAccount: boolean;
  settlementFund: SettlementFundAoAEnum | null;
  estate?: fromEstate.EstateState;
  trust?: fromTrust.TrustState;
  trustTaxInfo?: fromTrustTaxInfo.TrustTaxInfoState;
  draftId?: string;
  draftSequence?: number;
  backupWithholdingSignatureName?: string;
  backupWithholdingAnswer?: 'yes' | 'no';
}

export interface AdaptedState extends EntityState<TransactionState> {}

export interface State {
  accountRegistrations: fromAccountRegistrations.AccountRegistrationsState[];
  auth: fromAuth.AuthState;
  beneficiaries: fromBeneficiaries.BeneficiariesState;
  clientAccounts: fromClientAccounts.IClientAccountsState;
  content: fromContent.ContentState;
  costBasis: fromCostBasis.CostBasisState;
  decedentInfo: fromDecedentInfo.DecedentInfoState;
  documentUpload: fromDocumentUpload.IDocumentUploadState;
  draft: fromDraft.DraftState;
  letterUpload: fromLetterUpload.ILetterUploadState;
  taxDocumentUpload: fromTaxDocumentUpload.ITaxDocumentUploadState;
  estate: fromEstate.EstateState;
  recipientValidation: fromRecipientValidation.RecipientValidationState;
  featureToggles: FeatureToggleState;
  fraudDetectorSimulator: fromFraudDetectorSimulator.IFraudDetectorSimulatorState;
  fmf: fromFMF.IFMFState;
  gifting: fromGifting.GiftingState;
  incomeNetWorth: fromIncomeNetWorth.FinancialState;
  internalTransfer: fromInternalTransfer.InternalTransferState;
  itm: fromMultiIT.MultiITState;
  minorTransfer: fromMinorTransfer.MinorTransferState;
  mfePersonalInfoClient: fromMfePersonalInfoClient.MfePersonalInfoClientState;
  mfePersonalAddress: fromMfePersonalAddress.MfePersonalAddressState;
  mfePhone: fromMfePhone.MfePhoneState;
  mxm: fromMXM.MXMState;
  openAccount: fromOpenAccount.IOpenAccountState;
  otp: fromOneTimePasscodeState.OneTimePasscodeState;
  personalData: fromPersonalData.PersonalDataState;
  routerReducer: fromRouter.RouterReducerState<RouterStateUrl>;
  selectedClientAccounts: fromSelectedClientAccounts.ISelectedClientAccountsState;
  spinner: fromSpinner.ISpinnerState;
  submit: fromSubmit.ISubmitState;
  testMonkey: fromTestMonkey.TestMonkeyState;
  triage: fromTriage.TriageState;
  trust: fromTrust.TrustState;
  trustTaxInfo: fromTrustTaxInfo.TrustTaxInfoState;
  tcfTrust: fromTCFTrust.TCFTrustState;
  termsAccordion: fromTermsAccordion.TermsAccordionState;
  multiTermsAccordion: fromMultiTermsAccordion.MultiTermsAccordionState;
  experimentation: ExperimentationState;
  transactions: AdaptedState;
  currentTransactionId: string;
  isMultiReviewPageReachedAndEditMode: boolean;
  isMultiOAReviewPageReachedAndEditMode: boolean;
  isMultiReviewPageReachedAndSkipInvestmentRevision: boolean;
  accountTriage: fromAccountTriage.AccountTriageState;
  inheritedIra: fromInheritedIra.InheritedIraState;
  multiAccount: fromMultiAccount.MultiAccountState;
  adobeLaunch: fromAdobeLaunch.AdobeLaunchState;
  investmentObjectives: fromInvestmentObjectives.InvestmentState;
  org: fromOrg.OrgState;
}

export const reducers: ActionReducerMap<any> = {
  accountRegistrations: fromAccountRegistrations.reducer,
  auth: fromAuth.authReducer,
  beneficiaries: fromBeneficiaries.reducer,
  clientAccounts: fromClientAccounts.reducer,
  content: fromContent.reducer,
  costBasis: fromCostBasis.reducer,
  decedentInfo: fromDecedentInfo.reducer,
  documentUpload: fromDocumentUpload.reducer,
  draft: fromDraft.draftReducer,
  experimentation: fromExperimentation.reducer,
  letterUpload: fromLetterUpload.reducer,
  taxDocumentUpload: fromTaxDocumentUpload.reducer,
  estate: fromEstate.reducer,
  featureToggles: fromFeature.reducer,
  gifting: fromGifting.reducer,
  incomeNetWorth: fromIncomeNetWorth.reducer,
  fmf: fromFMF.reducer,
  internalTransfer: fromInternalTransfer.reducer,
  itm: fromMultiIT.reducer,
  minorTransfer: fromMinorTransfer.reducer,
  mfePersonalInfoClient: fromMfePersonalInfoClient.reducer,
  mfePersonalAddress: fromMfePersonalAddress.reducer,
  mfePhone: fromMfePhone.reducer,
  mockFraudDetector: fromFraudDetectorSimulator.reducer,
  mxm: fromMXM.reducer,
  otp: fromOneTimePasscodeState.reducer,
  openAccount: fromOpenAccount.reducer,
  openChoice: fromOpenChoice.reducer,
  personalData: fromPersonalData.reducer,
  routerReducer: fromRouter.routerReducer,
  selectedClientAccounts: fromSelectedClientAccounts.reducer,
  spinner: fromSpinner.reducer,
  submit: fromSubmit.reducer,
  testMonkey: fromTestMonkey.reducer,
  triage: fromTriage.reducer,
  trust: fromTrust.reducer,
  trustTaxInfo: fromTrustTaxInfo.reducer,
  tcfTrust: fromTCFTrust.reducer,
  termsAccordion: fromTermsAccordion.reducer,
  multiTermsAccordion: fromMultiTermsAccordion.reducer,
  recipientValidation: fromRecipientValidation.recipientValidationReducer,
  transactions: adaptedStateReducer,
  currentTransactionId: transactionIdReducer,
  isMultiReviewPageReachedAndEditMode: transactionsReviewEditModeReducer,
  isMultiOAReviewPageReachedAndEditMode: transactionsOAReviewEditModeReducer,
  isMultiReviewPageReachedAndSkipInvestmentRevision: transactionInvestmentObjectivesEditModeReducer,
  accountTriage: fromAccountTriage.reducer,
  inheritedIra: fromInheritedIra.reducer,
  multiAccount: fromMultiAccount.reducer,
  adobeLaunch: fromAdobeLaunch.reducer,
  investmentObjectives: fromInvestmentObjectives.reducer,
  org: fromOrg.reducer,
};

export const getAccountRegistrationsState =
  createFeatureSelector<fromAccountRegistrations.AccountRegistrationsState[]>(
    'accountRegistrations',
  );
export const getBeneficiariesState =
  createFeatureSelector<fromBeneficiaries.BeneficiariesState>('beneficiaries');
export const getClientAccountsState =
  createFeatureSelector<fromClientAccounts.IClientAccountsState>('clientAccounts');
//TODO: Remove content NGRX code when all files load content directly
// export const getContentState = createSelector(
//   (state: State) => state.content,
//   (value) => value,
// )
export const getCostBasis = createFeatureSelector<fromCostBasis.CostBasisState>('costBasis');
export const getDecedentInfoState =
  createFeatureSelector<fromDecedentInfo.DecedentInfoState>('decedentInfo');
export const getDocumentUploadState =
  createFeatureSelector<fromDocumentUpload.IDocumentUploadState>('documentUpload');
export const getLetterUploadState =
  createFeatureSelector<fromLetterUpload.ILetterUploadState>('letterUpload');
export const getTaxDocumentUploadState =
  createFeatureSelector<fromTaxDocumentUpload.ITaxDocumentUploadState>('taxDocumentUpload');
export const getEstateState = createFeatureSelector<fromEstate.EstateState>('estate');
export const getFeatureToggleState = createSelector(
  (state: State) => state.featureToggles,
  (value) => value,
);
export const getFMFState = createFeatureSelector<fromFMF.IFMFState>('fmf');
export const getFraudDetectorSimulatorState =
  createFeatureSelector<fromFraudDetectorSimulator.IFraudDetectorSimulatorState>(
    'mockFraudDetector',
  );
export const getGiftingState = createFeatureSelector<fromGifting.GiftingState>('gifting');
export const getIncomeNetWorthState =
  createFeatureSelector<fromIncomeNetWorth.FinancialState>('incomeNetWorth');
export const getInternalTransferState =
  createFeatureSelector<fromInternalTransfer.InternalTransferState>('internalTransfer');
export const getMinorTransferState =
  createFeatureSelector<fromMinorTransfer.MinorTransferState>('minorTransfer');
export const getMfePersonalInfoClientState =
  createFeatureSelector<fromMfePersonalInfoClient.MfePersonalInfoClientState>(
    'mfePersonalInfoClient',
  );
export const getMfePersonalAddressState =
  createFeatureSelector<fromMfePersonalAddress.MfePersonalAddressState>('mfePersonalAddress');
export const getMfePhoneState = createFeatureSelector<fromMfePhone.MfePhoneState>('mfePhone');
export const getMXMState = createFeatureSelector<fromMXM.MXMState>('mxm');
export const getOpenAccountState =
  createFeatureSelector<fromOpenAccount.IOpenAccountState>('openAccount');
export const getOpenChoiceState =
  createFeatureSelector<fromOpenChoice.OpenChoiceState>('openChoice');
export const getOTPState =
  createFeatureSelector<fromOneTimePasscodeState.OneTimePasscodeState>('otp');
export const getPersonalDataState =
  createFeatureSelector<fromPersonalData.PersonalDataState>('personalData');
export const getRouterState =
  createFeatureSelector<fromRouter.RouterReducerState<RouterStateUrl>>('routerReducer');
export const getSelectedClientAccountsState = createSelector(
  (state: State) => state.selectedClientAccounts,
  (value) => value,
);
export const getSpinnerState = createFeatureSelector<fromSpinner.ISpinnerState>('spinner');
export const getSubmitState = createFeatureSelector<fromSubmit.ISubmitState>('submit');
export const getTestMonkeyState =
  createFeatureSelector<fromTestMonkey.TestMonkeyState>('testMonkey');
export const getTriageState = createFeatureSelector<fromTriage.TriageState>('triage');
export const getTrustState = createFeatureSelector<fromTrust.TrustState>('trust');
export const getTrustTaxInfo =
  createFeatureSelector<fromTrustTaxInfo.TrustTaxInfoState>('trustTaxInfo');
export const getTCFTrustState = createFeatureSelector<fromTCFTrust.TCFTrustState>('tcfTrust');
export const getTermsAccordionState =
  createFeatureSelector<fromTermsAccordion.TermsAccordionState>('termsAccordion');
export const getMultiTermsAccordionState =
  createFeatureSelector<fromMultiTermsAccordion.MultiTermsAccordionState>('multiTermsAccordion');
export const getAccountTriageState =
  createFeatureSelector<fromAccountTriage.AccountTriageState>('accountTriage');
export const getExperimentationState =
  createFeatureSelector<ExperimentationState>('experimentation');
export const getInheritedIraState =
  createFeatureSelector<fromInheritedIra.InheritedIraState>('inheritedIra');
export const getIsReviewPageReachedAndEditMode = createFeatureSelector<boolean>(
  'isMultiReviewPageReachedAndEditMode',
);
export const getIsReviewPageReachedAndSkipInvestmentRevision = createFeatureSelector<boolean>(
  'isMultiReviewPageReachedAndSkipInvestmentRevision',
);
export const getMultiAccountState =
  createFeatureSelector<fromMultiAccount.MultiAccountState>('multiAccount');
export const getAdobeLaunchState = createFeatureSelector<AdobeLaunchState>('adobeLaunch');
export const getMultiITState = createFeatureSelector<fromMultiIT.MultiITState>('itm');
export const getInvestmentObjectivesState =
  createFeatureSelector<fromInvestmentObjectives.InvestmentState>(
    INVESTMENT_OBJECTIVES_FEATURE_KEY,
  );

@Injectable()
export class CustomSerializer implements fromRouter.RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    const { url } = routerState;
    const { queryParams } = routerState.root;
    let state: ActivatedRouteSnapshot = routerState.root;

    while (state.firstChild) {
      state = state.firstChild;
    }

    const { params } = state;

    return { url, queryParams, params };
  }
}
